import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AirportShuttleIcon from '@mui/icons-material/AirportShuttle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import FireTruckIcon from '@mui/icons-material/FireTruck';
import RvHookupIcon from '@mui/icons-material/RvHookup';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';

// apiSegment: matches the backend controller route prefix (/api/<apiSegment>/...).
// detailPath: URL prefix for the listing detail page (/<detailPath>/:id).
// addDetailsPath: URL for the multi-step "add listing" form for this type.
// Each vehicle type has its own tables/ids on the backend, so these are what
// let the frontend route a given tab/listing to the right backend + page.
//
// form: how MultiStepCarForm.js (the single, shared multi-step form) adapts
// its fields to this type — every type has its own dedicated "style" lookup
// and equipment catalog on the backend (never the shared Cars ones):
//   - styleLabel:     i18n key for the "style" select (bodyType/motoType/utilitaireBodyType)
//   - styleEndpoint:  path segment under /api/<apiSegment>/... for that lookup
//   - styleFormField: form state key (also becomes the FormData field name once capitalized)
//   - hasInteriorDetails/hasDoors: whether those fields make sense for this type
//   - hasCargoSpecs:  whether to show payload (kg) / cargo volume (m³) fields
//   - placeholders:   example values for numeric inputs, tuned per type so a
//                      moto listing doesn't suggest "1600cc" or a van "6 CV"
export const VEHICLE_TYPES = [
  {
    key: 'cars', i18n: 'vehicleTypes.cars', icon: DirectionsCarIcon, enabled: true,
    apiSegment: 'Cars', detailPath: 'cars-details', addDetailsPath: 'add-car-details', editPath: 'edit-car',
    // Valeur numérique attendue par BoostVehicleType côté backend (Car=0/Moto=1/Utilitaire=2).
    boostVehicleType: 0,
    form: {
      styleLabel: 'bodyType', styleEndpoint: 'body-types', styleFormField: 'bodyTypeId',
      hasInteriorDetails: true, hasDoors: true, hasCargoSpecs: false,
      placeholders: { power: 'ex. 6, 8...', engineCapacity: 'ex. 1600', price: 'ex. 120000' },
    },
  },
  {
    key: 'caravans', i18n: 'vehicleTypes.caravans', icon: AirportShuttleIcon, enabled: false,
    apiSegment: null, detailPath: null, addDetailsPath: null, form: null,
  },
  {
    key: 'vans', i18n: 'vehicleTypes.vans', icon: LocalShippingIcon, enabled: true,
    apiSegment: 'Utilitaires', detailPath: 'utilitaires-details', addDetailsPath: 'add-utilitaire-details', editPath: 'edit-utilitaire',
    boostVehicleType: 2,
    form: {
      styleLabel: 'utilitaireBodyType', styleEndpoint: 'body-types', styleFormField: 'bodyTypeId',
      hasInteriorDetails: true, hasDoors: true, hasCargoSpecs: true,
      placeholders: { power: 'ex. 8, 10...', engineCapacity: 'ex. 2000', price: 'ex. 150000' },
    },
  },
  {
    key: 'trucks', i18n: 'vehicleTypes.trucks', icon: FireTruckIcon, enabled: false,
    apiSegment: null, detailPath: null, addDetailsPath: null, form: null,
  },
  {
    key: 'trailers', i18n: 'vehicleTypes.trailers', icon: RvHookupIcon, enabled: false,
    apiSegment: null, detailPath: null, addDetailsPath: null, form: null,
  },
  {
    key: 'motorcycles', i18n: 'vehicleTypes.motorcycles', icon: TwoWheelerIcon, enabled: true,
    apiSegment: 'Motos', detailPath: 'motos-details', addDetailsPath: 'add-moto-details', editPath: 'edit-moto',
    boostVehicleType: 1,
    form: {
      styleLabel: 'motoType', styleEndpoint: 'moto-types', styleFormField: 'motoTypeId',
      hasInteriorDetails: false, hasDoors: false, hasCargoSpecs: false,
      placeholders: { power: 'ex. 15, 48, 100', engineCapacity: 'ex. 125, 650', price: 'ex. 35000' },
    },
  },
];

export const getVehicleTypeEntry = (key) => VEHICLE_TYPES.find(v => v.key === key) || VEHICLE_TYPES[0];

// Maps a detail-page URL prefix (cars-details/motos-details/utilitaires-details) back to a vehicle type entry.
export const getVehicleTypeByDetailPath = (detailPath) => VEHICLE_TYPES.find(v => v.detailPath === detailPath) || VEHICLE_TYPES[0];

// Maps an "add listing" URL prefix (add-car-details/add-moto-details/add-utilitaire-details) back to a vehicle type entry.
export const getVehicleTypeByAddPath = (addDetailsPath) => VEHICLE_TYPES.find(v => v.addDetailsPath === addDetailsPath) || VEHICLE_TYPES[0];

// Maps an "edit listing" URL prefix (edit-car/edit-moto/edit-utilitaire) back to a vehicle type entry.
export const getVehicleTypeByEditPath = (editPath) => VEHICLE_TYPES.find(v => v.editPath === editPath) || VEHICLE_TYPES[0];

// PascalCase the form-state field name to get the FormData/DTO field name (bodyTypeId -> BodyTypeId).
export const toDtoFieldName = (formField) => formField.charAt(0).toUpperCase() + formField.slice(1);
